/**
 * 問題バンク(`content/v{N}/quiz/{band}.json`)の読み取りヘルパー。
 *
 * 配信JSONは信用できない入力(古いキャッシュ・壊れた配信)として扱い、
 * `parseQuizBank` を通してから使う。
 */

import { validateQuizBank } from './schema.js';
import { findQuizStage } from './stages.js';
import type { QuizBank, QuizQuestion, QuizSubject, QuizUnit } from './types.js';

/** 問題バンクの検証エラー。 */
export class QuizSchemaError extends Error {
  constructor(readonly errors: readonly string[]) {
    super(`まなびモードの問題データが不正です:\n  - ${errors.join('\n  - ')}`);
    this.name = 'QuizSchemaError';
  }
}

/** 検証してから `QuizBank` として返す(不正なら例外)。 */
export function parseQuizBank(value: unknown, label = 'quiz'): QuizBank {
  const errors = validateQuizBank(value, label);
  if (errors.length > 0) throw new QuizSchemaError(errors);
  return value as QuizBank;
}

/** 問題を単元IDごとにまとめる(並びは引数の順のまま)。 */
export function groupQuestionsByUnit(
  questions: readonly QuizQuestion[],
): Map<string, QuizQuestion[]> {
  const byUnit = new Map<string, QuizQuestion[]>();
  for (const question of questions) {
    const bucket = byUnit.get(question.unit);
    if (bucket) bucket.push(question);
    else byUnit.set(question.unit, [question]);
  }
  return byUnit;
}

/** 単元を引く。 */
export function findQuizUnit(bank: QuizBank, unitId: string): QuizUnit | undefined {
  return bank.units.find((unit) => unit.id === unitId);
}

/** 単元の問題を `questionIds` の順で引く(解決できないIDは飛ばす)。 */
export function quizUnitQuestions(bank: QuizBank, unitId: string): QuizQuestion[] {
  const unit = findQuizUnit(bank, unitId);
  if (!unit) return [];
  const byId = new Map(bank.questions.map((question) => [question.id, question]));
  return unit.questionIds
    .map((id) => byId.get(id))
    .filter((question): question is QuizQuestion => question !== undefined);
}

/**
 * ステージの問題を**定義順で**引く(docs/13 §11。解決できないIDは飛ばす)。
 *
 * 出題はここで返った並びをそのまま使う(シャッフルしない)。
 * 単元・ステージが見つからないときは空配列。
 */
export function quizStageQuestions(
  bank: QuizBank,
  unitId: string,
  stageId: string,
): QuizQuestion[] {
  const unit = findQuizUnit(bank, unitId);
  if (!unit) return [];
  const stage = findQuizStage(unit, stageId);
  if (!stage) return [];
  const byId = new Map(bank.questions.map((question) => [question.id, question]));
  return stage.questionIds
    .map((id) => byId.get(id))
    .filter((question): question is QuizQuestion => question !== undefined);
}

/** 教科の単元を並び順で返す。 */
export function quizUnitsOfSubject(bank: QuizBank, subject: QuizSubject): QuizUnit[] {
  return bank.units.filter((unit) => unit.subject === subject);
}

/** バンクに出てくる教科(`units` の並び順・重複なし)。 */
export function quizSubjectsOf(bank: QuizBank): QuizSubject[] {
  return [...new Set(bank.units.map((unit) => unit.subject))];
}
